import { z } from 'zod';
import { packGenresSchema, packScenesSchema } from './production';
import { RulesSchema } from './rules';
import { PAYMENT_MIN_JPY } from './limits';
import { QUESTION_STATES, QUESTION_STATE_LABEL, type QuestionState } from './questionStates';

// REST の入力と出力。サーバーは入力をこれで検証し、画面は出力をこれで検証する。

export const ApiErrorSchema = z.object({ code: z.string(), message: z.string() });
export type ApiError = z.infer<typeof ApiErrorSchema>;

const email = z.string().trim().toLowerCase().email('メールアドレスの形が正しくありません').max(254);
const password = z.string().min(8, 'パスワードは8文字以上にしてください').max(200);

// ---------------------------------------------------------------- アカウント

export const SignupBody = z.object({
  email,
  password,
  /** 省略したらメールアドレスの @ より前を使う（あとで H-07 で変えられる） */
  displayName: z.string().trim().min(1, '表示名を入れてください').max(40).optional(),
  agree: z.literal(true, { error: '利用規約とプライバシーポリシーに同意してください' }),
});
export const LoginBody = z.object({ email, password: z.string().min(1).max(200), totp: z.string().optional() });
export const VerifyBody = z.object({ token: z.string().min(10).max(200) });
export const ForgotBody = z.object({ email });
export const ResetBody = z.object({ token: z.string().min(10).max(200), password });
export const MePatchBody = z.object({
  displayName: z.string().trim().min(1).max(40).optional(),
  email: email.optional(),
  currentPassword: z.string().max(200).optional(),
  newPassword: password.optional(),
  /** 新しい版や新しいパックのお知らせを受け取る */
  marketingOptIn: z.boolean().optional(),
});
export const UnsubscribeBody = z.object({ token: z.string().min(10).max(300) });
export const DeleteMeBody = z.object({ password: z.string().min(1).max(200) });

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(['host', 'admin']),
  isFree: z.boolean(),
  marketingOptIn: z.boolean(),
});
export type User = z.infer<typeof UserSchema>;

// ---------------------------------------------------------------- パック

export const PackSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  genre: z.string(),
  category: z.enum(['genre', 'exam']),
  audience: z.string(),
  difficulty: z.number(),
  kind: z.enum(['free', 'paid']),
  priceJpy: z.number(),
  questionCount: z.number(),
  publishedAt: z.string().nullable(),
  isNew: z.boolean(),
  /** 同じシリーズの版で同じ値 */
  seriesId: z.string(),
  editionNo: z.number(),
  editionLabel: z.string(),
  /** 新規に購入できるか（販売終了なら false）。無料パックは、いま受け取れるか（配布期間の中か） */
  onSale: z.boolean(),
  /** 無料パックの配布の締め切り（null は期限なし） */
  freeUntil: z.string().nullable().optional(),
  /**
   * 期間限定価格の予定（終了日時が過ぎていないものだけ）。期間中かは画面が時刻で判断する
   * （ホームページは書き出した後も終了日時で表示を戻せるように）。請求額はサーバーが決める
   */
  sale: z.object({ label: z.string(), priceJpy: z.number(), startsAt: z.string(), endsAt: z.string() }).nullable(),
  /** ジャンル（複数）と使う場面のタグ */
  genres: z.array(z.string()).default([]),
  scenes: z.array(z.string()).default([]),
  /** パッケージ画像の URL（サーバーからの相対。無ければ null） */
  coverUrl: z.string().nullable().default(null),
  /** ログイン中のみ：使えるかどうか */
  unlocked: z.boolean().optional(),
  /** ログイン中・未購入のみ：この人が今買うときの値段（サーバーが決める） */
  offer: z
    .object({
      listJpy: z.number(),
      amountJpy: z.number(),
      discount: z.enum(['none', 'upgrade', 'campaign']),
      fromEditionLabel: z.string().nullable(),
      until: z.string().nullable(),
    })
    .optional(),
});
export type PackSummary = z.infer<typeof PackSummarySchema>;

export const SampleQuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  answer: z.string(),
  audioUrl: z.string().nullable(),
  timings: z.array(z.object({ i: z.number(), t: z.number() })),
  durationMs: z.number(),
});
export type SampleQuestion = z.infer<typeof SampleQuestionSchema>;

/** 購入する前に確かめたいこと（入っている問題から集計） */
export const PackStatsSchema = z.object({
  /** 難易度ごとの問題数（1〜3） */
  difficulty: z.record(z.string(), z.number()),
  /** ジャンルごとの問題数（多い順） */
  genres: z.array(z.object({ name: z.string(), count: z.number() })),
  /** 1回20問で遊べる回数の目安 */
  games: z.number(),
});
export type PackStats = z.infer<typeof PackStatsSchema>;
/** 更新履歴（公開中のパックに問題を足した・外した記録。購入者には自動で届く） */
export const PackUpdateSchema = z.object({ at: z.string(), added: z.number(), removed: z.number(), note: z.string() });
export type PackUpdate = z.infer<typeof PackUpdateSchema>;
export const PackDetailSchema = PackSummarySchema.extend({ samples: z.array(SampleQuestionSchema), stats: PackStatsSchema.optional(), updates: z.array(PackUpdateSchema).default([]) });
export type PackDetail = z.infer<typeof PackDetailSchema>;

export const PackQuestionSchema = z.object({
  id: z.string(),
  position: z.number(),
  text: z.string(),
  answer: z.string(),
  genre: z.string(),
  difficulty: z.number(),
  hasAudio: z.boolean(),
  /** この主催者が大会で出した回数（2026-10-11） */
  plays: z.number().default(0),
});
export type PackQuestion = z.infer<typeof PackQuestionSchema>;

export const MeResponseSchema = z.object({ user: UserSchema, packs: z.array(PackSummarySchema) });
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ---------------------------------------------------------------- 大会

export const TeamInputSchema = z.object({
  name: z.string().trim().min(1).max(20),
  color: z.string().max(20),
});

/** ランダム出題：どのパックから何問 */
export const RandomPickSchema = z.object({ packId: z.string(), count: z.number().int().min(0).max(300) });
export type RandomPick = z.infer<typeof RandomPickSchema>;

/** 問題の前のひとこと（「もんだい。」など）。off：なし。fixed：選んだ1つ（空なら最初の1つ）。random：選んだ中から問題ごとに（空なら全部から） */
export const IntroSettingSchema = z.object({
  mode: z.enum(['off', 'fixed', 'random']),
  clipIds: z.array(z.string()).max(20),
});
export type IntroSetting = z.infer<typeof IntroSettingSchema>;
export const DEFAULT_INTRO: IntroSetting = { mode: 'fixed', clipIds: [] };
/** 問題の後ろのひとことは、既定では流さない（あとから足した機能のため） */
export const DEFAULT_OFF: IntroSetting = { mode: 'off', clipIds: [] };
/** 大会で選べるひとこと（主催者の設定画面用） */
export const IntroClipSummarySchema = z.object({ id: z.string(), text: z.string(), durationMs: z.number(), url: z.string() });
export type IntroClipSummary = z.infer<typeof IntroClipSummarySchema>;

export const TournamentOptionsSchema = z.object({
  step: z.number().int().min(1).max(4),
  packIds: z.array(z.string()),
  /** pack：選んだ問題を選んだ順に出す。random：random のパックごとの問題数を、受付を始めるときに選んで混ぜる */
  order: z.enum(['pack', 'random']),
  questionIds: z.array(z.string()).max(200),
  random: z.array(RandomPickSchema).max(50).default([]),
  teams: z.array(TeamInputSchema).max(8),
  intro: IntroSettingSchema.default(DEFAULT_INTRO),
  /** 正解発表の前（「正解は」）と後ろ（「です。」）。答えの音声とつなげて流す */
  answerPrefix: IntroSettingSchema.default(DEFAULT_INTRO),
  answerSuffix: IntroSettingSchema.default(DEFAULT_INTRO),
  /** 問題の後ろのひとこと（「さあ、どうぞ。」など）。問題を最後まで読んだときだけ流す */
  questionSuffix: IntroSettingSchema.default(DEFAULT_OFF),
});

/** 再開催。editionPackId を渡すと、その版（同じシリーズの新しい版）の問題で作り直す */
export const TournamentDuplicateBody = z.object({ editionPackId: z.string().optional() }).default({});

export const TournamentPatchBody = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  format: z.enum(['individual', 'team']).optional(),
  rules: RulesSchema.optional(),
  options: TournamentOptionsSchema.partial().optional(),
});

export const TournamentSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  format: z.enum(['individual', 'team']),
  status: z.enum(['draft', 'waiting', 'running', 'finished', 'abandoned']),
  questionCount: z.number(),
  participantCount: z.number(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  step: z.number(),
  winnerName: z.string().nullable(),
});
export type TournamentSummary = z.infer<typeof TournamentSummarySchema>;

export const TournamentDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  format: z.enum(['individual', 'team']),
  status: z.enum(['draft', 'waiting', 'running', 'finished', 'abandoned']),
  rules: RulesSchema,
  options: TournamentOptionsSchema,
  joinCode: z.string().nullable(),
  hostRemoteCode: z.string().nullable(),
  maxPlayers: z.number(),
  maxTeams: z.number(),
  createdAt: z.string(),
});
export type TournamentDetail = z.infer<typeof TournamentDetailSchema>;

export const JoinInfoSchema = z.object({
  tournamentId: z.string(),
  name: z.string(),
  format: z.enum(['individual', 'team']),
  accepting: z.boolean(),
  full: z.boolean(),
  status: z.enum(['draft', 'waiting', 'running', 'finished', 'abandoned']),
  teams: z.array(z.object({ id: z.string(), name: z.string(), color: z.string(), full: z.boolean() })),
});
export type JoinInfo = z.infer<typeof JoinInfoSchema>;

// ---------------------------------------------------------------- 運営（A-01〜A-05）

/** 問題の状態（studio.md 6章。questionStates.ts） */
export const QuestionStatusSchema = z.enum(QUESTION_STATES);
export type QuestionStatusName = QuestionState;
export const QUESTION_STATUS_LABEL = QUESTION_STATE_LABEL;

export const AdminQuestionQuery = z.object({
  status: QuestionStatusSchema.optional(),
  /** パック（紐付いたパック、または収録されたパック） */
  packId: z.string().max(64).optional(),
  /** パックに入っている問題だけ（パックのクイズプール。確認と音声の確認が済んだもの） */
  inPack: z.coerce.boolean().optional(),
  /** 収録クイズ（/packs/:id/pool）：パックに入っているもの（と変更の下書き）に、見本か・変更の下書きを付ける */
  packView: z.coerce.boolean().optional(),
  /** 収録クイズで、収録していない収録可・公開中のクイズを出す */
  outside: z.coerce.boolean().optional(),
  /** 範囲表の分野 */
  field: z.string().max(60).optional(),
  /** 承認（S4 までは確認した人）の有無 */
  stamped: z.enum(['yes', 'no']).optional(),
  genre: z.string().max(40).optional(),
  difficulty: z.coerce.number().int().min(1).max(3).optional(),
  q: z.string().max(100).optional(),
  page: z.coerce.number().int().min(1).max(1000).default(1),
  duplicates: z.coerce.boolean().optional(),
  /** 作問ジョブ */
  jobId: z.string().max(64).optional(),
  /** 似た問題ありの印 */
  similar: z.coerce.boolean().optional(),
  /** 並べ替え（既定は更新の新しい順） */
  /** position：パックの並び順（packId と一緒に。収録クイズ） */
  sort: z.enum(['updated', 'created', 'difficulty', 'status', 'answer', 'genre', 'text', 'position']).default('updated'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  /** 1ページの件数 */
  pageSize: z.coerce.number().int().refine((n) => [25, 50, 100].includes(n)).default(50),
});

/** 問題のまとめての操作（一覧でチェックした問題） */
export const QuestionBulkBody = z.discriminatedUnion('action', [
  /** delete はゴミ箱に入れる。restore はゴミ箱から戻す。purge はゴミ箱から完全に削除 */
  z.object({ action: z.literal('delete'), ids: z.array(z.string()).min(1).max(200) }),
  z.object({ action: z.literal('restore'), ids: z.array(z.string()).min(1).max(200) }),
  z.object({ action: z.literal('purge'), ids: z.array(z.string()).min(1).max(200) }),
  /** suspend は公開中 → 出題停止（非公開）。resume は出題停止 → 公開中 */
  z.object({ action: z.literal('suspend'), ids: z.array(z.string()).min(1).max(200), reason: z.string().trim().max(200).optional() }),
  z.object({ action: z.literal('resume'), ids: z.array(z.string()).min(1).max(200) }),
  z.object({ action: z.literal('genre'), ids: z.array(z.string()).min(1).max(200), genre: z.string().trim().min(1).max(20) }),
  z.object({ action: z.literal('difficulty'), ids: z.array(z.string()).min(1).max(200), difficulty: z.number().int().min(1).max(3) }),
]);

/** ジャンルのマスタ */
export const GenreBody = z.object({ name: z.string().trim().min(1, 'ジャンルの名前を入れてください').max(20, '20文字までにしてください') });
/** 対象のマスタ（2026-10-09） */
export const AudienceBody = z.object({ name: z.string().trim().min(1, '対象の名前を入れてください').max(20, '20文字までにしてください') });
export const AudiencePatchBody = z.object({ name: z.string().trim().min(1).max(20).optional(), sortOrder: z.number().int().min(0).max(10000).optional(), active: z.boolean().optional() });
/** パックの説明の自動生成：いまの説明の下書き */
export const DescribeBody = z.object({ draft: z.string().max(1000).default('') });
export const GenrePatchBody = z.object({ name: z.string().trim().min(1).max(20).optional(), sortOrder: z.number().int().min(0).max(10000).optional(), active: z.boolean().optional() });

export const QuestionPatchBody = z.object({
  /** どのパック・範囲表の項目のための問題か */
  targetPackId: z.string().max(64).nullable().optional(),
  scopeId: z.string().max(64).nullable().optional(),
  text: z.string().trim().min(1).max(200).optional(),
  reading: z.string().trim().min(1).max(300).optional(),
  answer: z.string().trim().min(1).max(60).optional(),
  answerReading: z.string().trim().max(80).optional(),
  answerAlts: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
  explanation: z.string().max(400).optional(),
  sources: z.array(z.string().url().max(500)).max(10).optional(),
  genre: z.string().max(20).optional(),
  audience: z.string().max(20).optional(),
  difficulty: z.number().int().min(1).max(3).optional(),
});

export const QuestionCreateBody = QuestionPatchBody.extend({
  /** 問題はパックの中で作る（2026-10-09）。あとから変えられない */
  targetPackId: z.string().min(1, 'パックを選んでください').max(64),
  text: z.string().trim().min(1).max(200),
  answer: z.string().trim().min(1).max(60),
});

/** 状態を変える（表にない移り変わりはサーバーが断る）。却下と出題停止は理由が要る */
export const QuestionStatusBody = z.object({ to: QuestionStatusSchema, reason: z.string().trim().max(500).default(''), /** 却下の理由のタグ（無ければ理由の先頭の「G1」などから取る） */ tags: z.array(z.string().max(20)).max(8).optional() });
export const QuestionApproveBody = z.object({ comment: z.string().trim().max(500).default('') });

export const ImportBody = z.object({
  /** 取り込む先のパック（問題はパックの中で作る） */
  packId: z.string().min(1).max(64),
  questions: z
    .array(
      z.object({
        text: z.string().min(1).max(200),
        reading: z.string().max(300).default(''),
        answer: z.string().min(1).max(60),
        answerReading: z.string().max(80).default(''),
        answerAlts: z.array(z.string().max(60)).max(10).default([]),
        explanation: z.string().max(400).default(''),
        sources: z.array(z.string().max(500)).max(10).default([]),
        genre: z.string().max(20).default(''),
        difficulty: z.number().int().min(1).max(3).default(2),
      }),
    )
    .min(1)
    .max(500),
});

export const AudioRequestBody = z.object({
  ids: z.array(z.string()).min(1).max(200),
  voiceProfileId: z.string().optional(),
  /** 読み方の指示（例：落ち着いた、はっきりした読み方） */
  style: z.string().max(100).optional(),
});

// generated = 不採用を取り消す
export const AudioReviewBody = z.object({ status: z.enum(['approved', 'rejected', 'generated']) });

/** 範囲表の1行（分野・項目・必要数・注意） */
export const ScopeRowSchema = z.object({
  id: z.string().max(64).optional(),
  field: z.string().trim().min(1, '分野を入れてください').max(60),
  item: z.string().trim().max(300).default(''),
  required: z.number().int().min(0).max(1000),
  note: z.string().trim().max(300).default(''),
});
export type ScopeRow = z.infer<typeof ScopeRowSchema>;
export const ScopeRowsBody = z.object({ rows: z.array(ScopeRowSchema).max(300) });
/** CSV（1行目は見出し：分野,項目,必要数,注意） */
export const ScopeCsvBody = z.object({ csv: z.string().min(1).max(200_000) });

export const PackPatchBody = z.object({
  /** シリーズの名前（同じシリーズの版すべてに付く） */
  seriesName: z.string().trim().min(1).max(60).optional(),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{2,60}$/, 'URL用の名前は半角英小文字・数字・ハイフンで入れてください')
    .optional(),
  title: z.string().trim().min(1).max(60).optional(),
  description: z.string().max(1000).optional(),
  genre: z.string().max(20).optional(),
  category: z.enum(['genre', 'exam']).optional(),
  genres: packGenresSchema.optional(),
  /** 使う場面のタグ */
  scenes: packScenesSchema.optional(),
  /** 詳細・キーワード（作問の指示に使う） */
  theme: z.string().trim().max(500).optional(),
  audience: z.string().max(20).optional(),
  kind: z.enum(['free', 'paid']).optional(),
  /** 無料は 0。有料は 50 円以上（PAY.JP の最低金額） */
  priceJpy: z.number().int().min(0).max(100000).refine((v) => v === 0 || v >= PAYMENT_MIN_JPY, `有料の値段は${PAYMENT_MIN_JPY}円以上にしてください（決済サービスの最低金額）`).optional(),
  sortOrder: z.number().int().optional(),
  questions: z.array(z.object({ questionId: z.string(), isSample: z.boolean() })).max(500).optional(),
  editionLabel: z.string().trim().min(1, '版の名前を入れてください').max(30).optional(),
  saleStatus: z.enum(['on_sale', 'ended']).optional(),
  /** 空（null）は割引なし */
  upgradePriceJpy: z.number().int().min(PAYMENT_MIN_JPY, `乗り換え価格は${PAYMENT_MIN_JPY}円以上にしてください`).max(100000).nullable().optional(),
  /** 空（null）は無期限 */
  upgradeUntil: z.iso.datetime({ offset: true }).nullable().optional(),
  /** 期間限定価格。空（null）は設定なし */
  salePriceJpy: z.number().int().min(PAYMENT_MIN_JPY, `期間限定価格は${PAYMENT_MIN_JPY}円以上にしてください`).max(100000).nullable().optional(),
  saleStartsAt: z.iso.datetime({ offset: true }).nullable().optional(),
  saleEndsAt: z.iso.datetime({ offset: true }).nullable().optional(),
  saleLabel: z.string().trim().min(1).max(20).optional(),
  /** 無料パックの配布期間。空（null）は期限なし */
  freeFrom: z.iso.datetime({ offset: true }).nullable().optional(),
  freeUntil: z.iso.datetime({ offset: true }).nullable().optional(),
});
export const NewEditionBody = z.object({ editionLabel: z.string().trim().min(1).max(30).optional() });

export const VoiceBody = z.object({
  name: z.string().trim().min(1).max(40),
  provider: z.string().max(20),
  model: z.string().max(60),
  providerVoiceId: z.string().max(200),
  /** 声の演技の指示（この声で作る音声すべてに同じものを使う） */
  stylePrompt: z.string().trim().max(300).default(''),
  expiresAt: z.string().datetime().nullable(),
  isDefault: z.boolean().default(false),
});

// ---------------------------------------------------------------- 購入（H-02・H-07・A-05）

export const PaymentMethodSchema = z.enum(['card', 'apple_pay', 'paypay']);
export const PAYMENT_METHOD_LABEL: Record<z.infer<typeof PaymentMethodSchema>, string> = {
  card: 'クレジットカード',
  apple_pay: 'Apple Pay',
  paypay: 'PayPay',
};
/** 金額は受け取らない（送られてきても捨てる）。サーバーが版と持っているパックから決める */
export const OrderCreateBody = z.object({ packId: z.string(), method: PaymentMethodSchema });
export const OrderConfirmBody = z.object({ token: z.string().max(200).optional() });
export const ReceiptNameBody = z.object({ receiptName: z.string().trim().max(60) });
export const RefundBody = z.object({ revoke: z.boolean() });

/** 領収書・領収メール・特定商取引法のページ・よくある質問に載せる一文 */
export const INVOICE_NOTE = '当方は適格請求書発行事業者ではないため、登録番号の記載はありません。';
/** 購入画面の決済ボタンの近くに出す */
export const NO_REFUND_NOTE = 'デジタル商品のため、購入後の返品・返金はお受けしていません。';

export type OrderView = {
  id: string;
  packId: string;
  packTitle: string;
  editionLabel: string;
  /** 実際の請求額 */
  amountJpy: number;
  /** 通常価格 */
  listPriceJpy: number;
  discountKind: 'none' | 'upgrade' | 'campaign';
  /** 乗り換えの根拠になった前の版の名前 */
  upgradeFromLabel: string | null;
  method: 'card' | 'apple_pay' | 'paypay';
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paidAt: string | null;
  receiptNo: string;
  createdAt: string;
};

/** 画面のエラーの報告（POST /client-errors）。サーバーはログに残すだけ */
export const ClientErrorBody = z.object({
  kind: z.string().max(40),
  message: z.string().max(500),
  stack: z.string().max(2000),
  url: z.string().max(300),
  ua: z.string().max(300),
});
